/**
 * Wiki-tuned Table extensions — extend the canonical TipTap Table /
 * TableCell / TableHeader trio with two attribute axes the wiki surface
 * needs:
 *
 *   - per-cell `textAlign` (`left | center | right | null`) on both
 *     `tableCell` and `tableHeader`. Drives the per-cell text-alignment
 *     buttons in the existing `<TableBubbleMenu>`. Persists as a
 *     `data-text-align="..."` attribute + inline `style="text-align:..."`
 *     so a re-mounted editor reads it back and a flat HTML render
 *     (e.g. read-only doc page) shows the alignment without needing the
 *     editor's CSS rules.
 *
 *   - table-level `headerBgColor` on `table` itself — a slug from the
 *     unified 13-color `CUSTOM_PANEL_COLORS` palette OR `null` for "no
 *     tint" (the default `bg-bg-muted` header style stays). Drives the
 *     <TableFooterPopover>'s header-color picker. Stored as a `data-
 *     header-bg-color="..."` attribute on the rendered `<table>` so a
 *     CSS rule on the editor can paint the header row by descending
 *     into `[data-header-bg-color="..."] th`. Storing the slug (not a
 *     hex) keeps the document portable + theme-token-aware, matching
 *     the panel-kind palette decision.
 *
 * Cell content schema:
 *   TipTap's default `TableCell.content` is `block+`. We narrow it to the
 *   explicit `TABLE_CELL_CONTENT` list below for three reasons:
 *
 *     1. `table` is excluded, so a table can never nest inside a cell.
 *        Nothing renders a nested table, the structural menus have no way
 *        to address one, and every markdown projection would have to
 *        invent a shape for it.
 *     2. `paragraph` leads the list because `createAndFill` — which
 *        `insertTable`, `addRowAfter` and `addColumnBefore` all run —
 *        fills a required cell with the FIRST type that satisfies the
 *        expression. An empty cell has to be an empty paragraph, never an
 *        empty list.
 *     3. A surface may only name nodes IT registers. ProseMirror throws
 *        while building the schema otherwise, so the list is built per
 *        surface: the wiki adds its callout panels, expand sections and
 *        video embeds, and the card description — which registers none of
 *        them — must not name them or every board that opens a card would
 *        crash. This is why the cell extensions are factories rather than
 *        shared constants.
 *
 *   The previous constraint was a single `paragraph`, which made Enter a
 *   no-op inside a cell and made any paste whose cells carry two blocks
 *   spill the overflow sideways into the next cell. It existed so
 *   `tiptap-markdown`'s GFM serializer wouldn't fall back to `[table]`;
 *   that reason is gone twice over — bodies persist as TipTap JSON, and
 *   the clipboard's plain-text projection is now ours (`table-markdown.ts`).
 *
 * Markdown round-trip note:
 *   `textAlign` is lossy through GFM markdown (GFM only supports
 *   column-level alignment via the `:---:` separator row, not per-cell).
 *   Wiki Phase 4 docs persist as TipTap JSON (see
 *   `DocPage.tsx:181 → storageFormat: 'json'`), so the per-cell
 *   alignment attribute survives a save+load. Legacy markdown wiki docs
 *   (`storageFormat: 'md'`) won't carry the alignment — same trade-off
 *   the doc-page already accepts for panels / expand / embeds.
 *
 *   `headerBgColor` follows the same shape: lossy through markdown,
 *   stable through JSON. The default (no tint) renders identically in
 *   both formats so a legacy doc keeps reading correctly.
 */

import Table from '@tiptap/extension-table'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import TableRow from '@tiptap/extension-table-row'
import { columnResizing, tableEditing } from '@tiptap/pm/tables'

import { WikiTableView } from './table-view'

import type { EditorSurface } from '../../extensions'

import {
  CUSTOM_PANEL_COLORS,
  type CustomPanelColor,
  isCustomPanelColor,
} from '@/editor/nodes/panels/panel-kinds'

import { serializeTable, serializeTableCell, serializeTableRow } from './table-markdown'

/**
 * `null` is "no tint" — the header row then paints TRANSPARENT and reads
 * like the body cells, distinguished only by its bold text. That is a
 * deliberate product decision, not an oversight: an implicit grey fill on
 * every new table was rejected. Any value from the unified panel palette
 * paints the header row instead.
 */
export type TableHeaderBgColor = CustomPanelColor | null

export const TABLE_HEADER_BG_COLORS: readonly CustomPanelColor[] = CUSTOM_PANEL_COLORS

/**
 * What a table cell may hold, per surface.
 *
 * Three rules govern every list below. `paragraph` MUST stay first, because
 * `createAndFill` picks the first satisfying type when it fills an empty cell.
 * `table` MUST stay absent everywhere — no nested tables. And a surface's list
 * may only name nodes that surface actually registers: ProseMirror throws
 * while building the schema otherwise, so listing a wiki-only panel under
 * `card` would crash every board that opens a card.
 *
 * The lists are otherwise as WIDE as each surface allows, because a cell is a
 * small document and an author reaching for `/info` inside one has no reason
 * to expect it to silently do nothing. Keeping them wide is what
 * `table-slash-coverage.test.ts` enforces: it runs every wiki slash command
 * inside a cell and fails when one no-ops, so a newly added block type cannot
 * quietly become un-insertable here.
 */
const SHARED_CELL_BLOCKS = [
  'paragraph',
  'heading',
  'bulletList',
  'orderedList',
  'taskList',
  'codeBlock',
  'blockquote',
  'image',
  'horizontalRule',
  // Registered on wiki AND on card / comment, so a video dropped with the
  // caret inside a cell lands in the cell rather than being fitted
  // somewhere surprising.
  'videoEmbed',
] as const

/** Registered only on the wiki surface. */
const WIKI_CELL_BLOCKS = [
  'infoPanel',
  'notePanel',
  'successPanel',
  'warningPanel',
  'errorPanel',
  'customPanel',
  'expandSection',
] as const

export function tableCellContent(surface: EditorSurface): string {
  const blocks =
    surface === 'wiki' ? [...SHARED_CELL_BLOCKS, ...WIKI_CELL_BLOCKS] : SHARED_CELL_BLOCKS
  return `(${blocks.join(' | ')})+`
}

/** Per-cell text-alignment values. `null` = inherit cell default. */
export type CellTextAlign = 'left' | 'center' | 'right' | null

const CELL_TEXT_ALIGNS: readonly Exclude<CellTextAlign, null>[] = ['left', 'center', 'right']

function isCellTextAlign(value: unknown): value is Exclude<CellTextAlign, null> {
  return typeof value === 'string' && (CELL_TEXT_ALIGNS as readonly string[]).includes(value)
}

/**
 * Shared `textAlign` attribute spec — added to both cell + header. The
 * `style` write keeps the rendered HTML readable without the editor's
 * CSS rules (matters for the read-only doc-page render path); the
 * `data-text-align` mirror is what `parseHTML` reads back so we don't
 * have to parse a CSS string.
 */
function buildTextAlignAttribute() {
  return {
    textAlign: {
      default: null as CellTextAlign,
      parseHTML: (el: HTMLElement): CellTextAlign => {
        const attr = el.getAttribute('data-text-align')
        if (isCellTextAlign(attr)) return attr
        // Fall back to inline style if the data attribute is missing
        // (paste from another wiki doc, legacy serialized cell, etc.).
        const styleAlign = el.style.textAlign
        if (isCellTextAlign(styleAlign)) return styleAlign
        return null
      },
      renderHTML: (attrs: { textAlign: CellTextAlign }) => {
        if (!attrs.textAlign) return {}
        return {
          'data-text-align': attrs.textAlign,
          style: `text-align: ${attrs.textAlign}`,
        }
      },
    },
  }
}

/**
 * `buildWikiTableCell` — `TableCell` + the surface's cell content model +
 * a `textAlign` attribute. A factory rather than a constant because the
 * content model names wiki-only blocks on the wiki surface, and a schema that
 * never registered them throws on construction. Keeps the original `addAttributes`
 * (colspan/rowspan/colwidth) by spreading the parent's attribute set,
 * then layering `textAlign` on top.
 */
export function buildWikiTableCell(surface: EditorSurface) {
  return TableCell.extend({
    content: tableCellContent(surface),
    addStorage() {
      return { markdown: { serialize: serializeTableCell } }
    },
    addAttributes() {
      const parent = this.parent?.() ?? {}
      return {
        ...parent,
        ...buildTextAlignAttribute(),
      }
    },
  })
}

/**
 * `buildWikiTableHeader` — same shape as `buildWikiTableCell`, for header rows.
 * The header background color is NOT here; it's a table-level attribute
 * on `WikiTable` because the bg paints the entire first row uniformly
 * (per-header-cell color would diverge from the "header row identity"
 * the picker is designed to control).
 */
export function buildWikiTableHeader(surface: EditorSurface) {
  return TableHeader.extend({
    content: tableCellContent(surface),
    addStorage() {
      return { markdown: { serialize: serializeTableCell } }
    },
    addAttributes() {
      const parent = this.parent?.() ?? {}
      return {
        ...parent,
        ...buildTextAlignAttribute(),
      }
    },
  })
}

/**
 * `WikiTable` — `Table` + `headerBgColor` attribute. The attribute
 * renders as `data-header-bg-color="<slug>"` on the `<table>` element;
 * the editor's CSS (added to `global.css` in the same change) paints
 * `[data-header-bg-color] th` with the matching tint. Unknown / null
 * values render nothing → the header stays transparent, which is the
 * deliberate default (see `TableHeaderBgColor` above).
 */
export const WikiTable = Table.extend({
  addStorage() {
    return { markdown: { serialize: serializeTable } }
  },
  /**
   * The stock plugin list, minus one gate.
   *
   * `Table.addProseMirrorPlugins` installs column resizing only when
   * `this.editor.isEditable` — evaluated ONCE, while plugins are being
   * built. `editor.setEditable()` routes through `setOptions()`, which
   * updates props and state but never rebuilds the plugin list, and the
   * wiki doc editor deliberately mounts read-only and flips to editable
   * in place (keying it on `mode` instead tears down tippy popovers
   * mid-flight). So the stock gate leaves every view-first editor
   * permanently unresizable, and — because the resize plugin is what
   * installs the node view — without the wrapper element that gives a
   * wide table its horizontal scroll.
   *
   * Installing it unconditionally is safe: prosemirror-tables' own
   * pointer handlers each return early while `view.editable` is false,
   * so a read-only table gets the node view and no drag affordance.
   */
  addProseMirrorPlugins() {
    return [
      ...(this.options.resizable
        ? [
            columnResizing({
              handleWidth: this.options.handleWidth,
              cellMinWidth: this.options.cellMinWidth,
              defaultCellMinWidth: this.options.cellMinWidth,
              // Our own view, because the stock one drops the node's
              // attributes and the header tint is one of them.
              View: WikiTableView,
              lastColumnResizable: this.options.lastColumnResizable,
            }),
          ]
        : []),
      tableEditing({ allowTableNodeSelection: this.options.allowTableNodeSelection }),
    ]
  },
  addAttributes() {
    const parent = this.parent?.() ?? {}
    return {
      ...parent,
      headerBgColor: {
        default: null as TableHeaderBgColor,
        parseHTML: (el: HTMLElement): TableHeaderBgColor => {
          const attr = el.getAttribute('data-header-bg-color')
          if (isCustomPanelColor(attr)) return attr
          return null
        },
        renderHTML: (attrs: { headerBgColor: TableHeaderBgColor }) => {
          if (!attrs.headerBgColor) return {}
          return { 'data-header-bg-color': attrs.headerBgColor }
        },
      },
    }
  },
})

/**
 * `WikiTableRow` — the stock row plus a markdown serializer.
 *
 * A row carries no attributes of its own; this variant exists only so a
 * PARTIAL cell selection has a serializer to reach. Copying one cell,
 * one column or a few body rows hands the clipboard a bare fragment of
 * rows with no `table` wrapper, and `tiptap-markdown` ships no row spec,
 * so without this the plain-text half of the clipboard is the literal
 * string `[tableRow]` repeated once per row.
 */
export const WikiTableRow = TableRow.extend({
  addStorage() {
    return { markdown: { serialize: serializeTableRow } }
  },
})
